import { mountEscapeButton } from '../escape-button';
import { pageShell, academicButton } from '../ui/components';
import { extractVocabFromImage, type ExtractedVocab } from '../exam-upload/ocr-client';
import { loadVocab, addVocabBatch, type VocabWord } from '../../shared/vocab-store';

/**
 * 拍單字表批次匯入(老闆/家長一次選多張單字表照片,自動辨識成單字)。
 *
 * 流程:
 *   <input type=file multiple accept=image/*> 一次選多張
 *   → 逐張 extractVocabFromImage(顯示「辨識中 N/M」)
 *   → 彙整 {word,zh},去重(by word 小寫,先到者保留)
 *   → 預覽清單(可逐筆刪掉誤抽的)
 *   → 「全部加入單字庫」→ addVocabBatch → 顯示「已加入 N 個單字」
 *
 * 視覺 = 學術風格設計系統(src/learn/ui):紙感頁殼 `pageShell` + 學術按鈕
 * `academicButton`(全部加入 = primary、返回 = secondary、刪除 = danger);見 wiki page-470f59c0。
 * 學習介面要專業(boss 2026-05-23):無音效、無 mascot、無彩光、文案中性。
 * 學習/遊戲分離鐵則:只動單字資料,不碰 commanderXp / 戰鬥數值。
 */

/** 去重彙整:同一 word(小寫)只留第一筆;空 word 丟棄。 */
function dedupeVocab(items: ExtractedVocab[]): ExtractedVocab[] {
  const seen = new Set<string>();
  const out: ExtractedVocab[] = [];
  for (const it of items) {
    const word = (it.word ?? '').trim();
    if (word.length === 0) continue;
    const key = word.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ word, zh: (it.zh ?? '').trim() });
  }
  return out;
}

export function runVocabPhotoImport(container: HTMLElement, onDone: () => void): void {
  const unmountEscape = mountEscapeButton(onDone);

  container.innerHTML = '';

  const shell = pageShell(container, {
    title: '拍單字表匯入',
    subtitle: '選擇一張或多張單字表照片,系統會自動辨識英文單字與中文意思,確認後加入單字庫。',
  });
  shell.root.querySelector('.ef-title')?.classList.add('vocab-photo-import__title');
  shell.root.querySelector('.ef-subtitle')?.classList.add('vocab-photo-import__help');

  const root = document.createElement('div');
  root.className = 'vocab-photo-import';
  root.style.cssText = 'display:flex;flex-direction:column;gap:14px;';
  shell.body.appendChild(root);

  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = 'image/*';
  fileInput.multiple = true;
  // 不要設 capture:iOS 上 capture 會強制開相機、只能拍一張、無法從相簿選/多選。
  // 不設 → 開檔案選擇器,可從相簿一次多選(相機仍是選項之一)。
  fileInput.className = 'vocab-photo-import__file';
  fileInput.setAttribute('data-testid', 'vocab-photo-file');
  fileInput.style.cssText =
    'padding:12px 14px;background:var(--ef-paper-bright);border:1px dashed var(--ef-line);' +
    'border-radius:var(--ef-radius-sm);color:var(--ef-ink-soft);font-family:var(--ef-sans);font-size:14px;';
  root.appendChild(fileInput);

  const status = document.createElement('p');
  status.className = 'vocab-photo-import__status';
  status.setAttribute('data-testid', 'vocab-photo-status');
  status.style.cssText = 'margin:0;color:var(--ef-sub);font-size:14px;line-height:1.6;';
  root.appendChild(status);

  // ── 預覽清單 ────────────────────────────────────────────────────
  const listEl = document.createElement('ul');
  listEl.className = 'vocab-photo-import__list';
  listEl.setAttribute('data-testid', 'vocab-photo-list');
  listEl.style.cssText = 'list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:8px;';
  root.appendChild(listEl);

  // ── 動作列:全部加入(primary)+ 返回(secondary)──
  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;flex-wrap:wrap;gap:10px;';
  root.appendChild(actions);

  const addAllBtn = academicButton({
    label: '✅ 全部加入單字庫',
    variant: 'primary',
    onClick: () => {
      if (pending.length === 0) return;
      const words: VocabWord[] = pending.map((w) => ({ word: w.word, zh: w.zh }));
      addVocabBatch([...loadVocab(), ...words]);
      const added = pending.length;
      pending = [];
      renderList();
      status.textContent = `已加入 ${added} 個單字。`;
      addAllBtn.disabled = true;
    },
  });
  addAllBtn.classList.add('vocab-photo-import__add-all');
  addAllBtn.setAttribute('data-testid', 'vocab-photo-add-all');
  addAllBtn.disabled = true;
  actions.appendChild(addAllBtn);

  const backBtn = academicButton({
    label: '返回',
    variant: 'secondary',
    onClick: () => {
      unmountEscape();
      onDone();
    },
  });
  backBtn.classList.add('vocab-photo-import__back');
  backBtn.setAttribute('data-testid', 'vocab-photo-back');
  actions.appendChild(backBtn);

  /** 目前彙整待確認的單字(可被逐筆刪除)。 */
  let pending: ExtractedVocab[] = [];

  function renderList(): void {
    listEl.innerHTML = '';
    addAllBtn.disabled = pending.length === 0;

    if (pending.length === 0) {
      return;
    }

    pending.forEach((w, i) => {
      const li = document.createElement('li');
      li.className = 'vocab-photo-import__item';
      li.setAttribute('data-word', w.word);
      li.style.cssText =
        'display:flex;align-items:center;gap:12px;padding:10px 14px;background:var(--ef-paper-bright);' +
        'border:1px solid var(--ef-line);border-radius:var(--ef-radius-sm);';

      const text = document.createElement('span');
      text.className = 'vocab-photo-import__item-text';
      text.textContent = w.zh ? `${w.word} — ${w.zh}` : w.word;
      text.style.cssText = 'flex:1;min-width:0;color:var(--ef-ink-soft);font-size:15px;';
      li.appendChild(text);

      const del = academicButton({
        label: '刪除',
        variant: 'danger',
        onClick: () => {
          pending.splice(i, 1);
          renderList();
        },
      });
      del.classList.add('vocab-photo-import__del');
      del.setAttribute('data-action', 'delete');
      del.setAttribute('aria-label', `刪除 ${w.word}`);
      del.style.cssText = 'min-height:34px;padding:6px 16px;font-size:14px;';
      li.appendChild(del);

      listEl.appendChild(li);
    });
  }

  fileInput.addEventListener('change', async () => {
    const files = Array.from(fileInput.files ?? []);
    if (files.length === 0) return;

    fileInput.disabled = true;
    addAllBtn.disabled = true;
    const collected: ExtractedVocab[] = [];

    for (let i = 0; i < files.length; i++) {
      status.textContent = `辨識中 ${i + 1}/${files.length}…`;
      const got = await extractVocabFromImage(files[i]);
      collected.push(...got);
    }

    pending = dedupeVocab(collected);
    fileInput.disabled = false;

    if (pending.length === 0) {
      status.textContent = '沒有辨識到單字,請換一張更清楚的照片再試。';
    } else {
      status.textContent = `辨識到 ${pending.length} 個單字,確認後加入。`;
    }
    renderList();
  });

  renderList();
}
